/**
 * Centralized API client for making HTTP requests
 *
 * Features:
 * - Automatic CSRF token inclusion
 * - Consistent error handling
 * - TypeScript type safety
 * - JSON response parsing
 *
 * Usage:
 * ```typescript
 * import { ApiClient } from '../utils/api'
 * import type { Event, EventResponse } from '../types/api'
 *
 * const event = await ApiClient.get<Event>('/events/1.json')
 * const result = await ApiClient.post<Event>('/events', { event: { name: 'Wedding' } })
 * ```
 */

// Re-export commonly used types from types/api.ts for convenience
export type {
  ApiResponse,
  ApiSuccessResponse,
  ApiErrorResponse,
} from '../types/api'

/**
 * API Error with HTTP status
 */
export interface ApiError {
  message: string
  status: number
  errors?: Record<string, string[]>
}

export class ApiClient {
  /**
   * Get CSRF token from meta tag
   * @private
   */
  private static getCSRFToken(): string {
    const metaTag = document.querySelector('meta[name="csrf-token"]')
    return metaTag?.getAttribute('content') || ''
  }

  /**
   * Get base headers for all requests
   * @private
   */
  private static getBaseHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': this.getCSRFToken(),
      Accept: 'application/json',
    }
  }

  /**
   * Handle fetch errors consistently
   * @private
   */
  private static async handleError(response: Response): Promise<never> {
    let errorData: any
    try {
      errorData = await response.json()
    } catch {
      errorData = { error: `HTTP ${response.status}: ${response.statusText}` }
    }

    const error: ApiError = {
      message: errorData.error || errorData.message || 'An error occurred',
      status: response.status,
      errors: errorData.errors,
    }

    throw error
  }

  /**
   * Make a GET request
   * @param url - The URL to fetch
   * @returns Promise with typed response
   */
  static async get<T = any>(url: string): Promise<T> {
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getBaseHeaders(),
    })

    if (!response.ok) {
      await this.handleError(response)
    }

    return response.json()
  }

  /**
   * Make a POST request
   * @param url - The URL to send the request to
   * @param data - The data to send
   * @returns Promise with typed response
   */
  static async post<T = any>(url: string, data: any): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getBaseHeaders(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      await this.handleError(response)
    }

    return response.json()
  }

  /**
   * Make a PATCH request
   * @param url - The URL to update
   * @param data - The data to send
   * @returns Promise with typed response
   */
  static async patch<T = any>(url: string, data: any): Promise<T> {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getBaseHeaders(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      await this.handleError(response)
    }

    return response.json()
  }

  /**
   * Make a PUT request
   * @param url - The URL to update
   * @param data - The data to send
   * @returns Promise with typed response
   */
  static async put<T = any>(url: string, data: any): Promise<T> {
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getBaseHeaders(),
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      await this.handleError(response)
    }

    return response.json()
  }

  /**
   * Make a DELETE request
   * @param url - The URL to delete
   * @returns Promise with typed response
   */
  static async delete<T = any>(url: string): Promise<T> {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getBaseHeaders(),
    })

    if (!response.ok) {
      await this.handleError(response)
    }

    // DELETE might return empty body
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return response.json()
    }
    return {} as T
  }

  /**
   * Make a request with custom options
   * @param url - The URL to fetch
   * @param options - Custom fetch options
   * @returns Promise with typed response
   */
  static async fetch<T = any>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getBaseHeaders(),
        ...options.headers,
      },
    })

    if (!response.ok) {
      await this.handleError(response)
    }

    return response.json()
  }
}
